import { FC, useEffect, useMemo, useRef, useState } from "react";
import { History } from "history";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import Chip from "@mui/material/Chip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import type { UserAircraftOperationRecord } from "src/shared/api-types/aircraft";
import type { AircraftOverviewRow } from "src/shared/api-types/certificate";
import type { AssignedLimitation } from "src/shared/api-types/limitations";
import { SettingKey } from "src/shared/api-types/user";

import { getAircraftOperationRecords } from "src/api/aircraft";
import { ComplianceWithCoursesResponseHydrated as ComplianceWithCourses } from "src/api/compliance";
import { getDefaultSettings, saveDefaultSettings } from "src/api/settings";
import useFeatureFlag, { FEATURE_FLAGS } from "src/hooks/useFeatureFlag";

import ExpandableSection from "../../../components/ExpandableSection";
import ExpandCollapseButton from "../../../components/ExpandCollapseButton";
import CertificateList from "../../../components/Certificate/CertificateList";
import CourseStatusIcon from "../../../components/CourseStatusIcon";
import Hint from "../../../components/Hint";
import { useExpandableState } from "../../../hooks/useExpandableState";
import { formatDate } from "../../../services/formatters";
import { scrollToElement } from "../../../services/scrollToElement";
import aircraftByOperationImg from "../../../assets/images/aircraft-by-operation.png";
import { getWorstStatusNode } from "../utils/statusAggregation";

// ─── Data structures ────────────────────────────────────────────────────────

interface AircraftSubRow {
  id: number;
  name: string;
  status: string | null;
  validUntil: string | null;
  courseIds: string[];
  underSupervision?: boolean;
  limitations?: AssignedLimitation[];
}

interface OperationCard {
  id: number;
  name: string;
  positionId: number;
  positionName: string;
  roleName: string;
  roleId: number;
  operationStatus: string | null;
  operationValidUntil: string | null;
  aircraft: AircraftSubRow[];
}

interface PositionGroup {
  key: string;
  roleName: string;
  positionName: string;
  operations: OperationCard[];
}

interface EnabledOperationTracking {
  operationKeys: Set<string>;
  aircraftTypeIds: Set<number>;
}

// ─── Title components ────────────────────────────────────────────────────────

interface OperationCardTitleProps {
  operation: OperationCard;
  crewId?: number;
  userId?: number;
  history?: History;
}

const OperationCardTitle: FC<OperationCardTitleProps> = ({ operation, crewId, userId, history }) => {
  const operationsPath =
    crewId && userId ? `/crew/${crewId}/members/${userId}/compliance/operations` : `/user/compliance/operations`;
  const hash = `operation-${operation.id}`;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (history) {
      history.push({ pathname: operationsPath, hash });
    } else {
      window.location.href = `${operationsPath}#${hash}`;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CourseStatusIcon status={operation.operationStatus} />
        <MuiLink
          component="button"
          variant="h6"
          underline="always"
          onClick={handleClick}
          sx={{
            fontWeight: 600,
            textAlign: "left",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          {operation.name}
        </MuiLink>
      </Box>
      {operation.operationValidUntil && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            ml: 2,
            whiteSpace: "nowrap",
          }}
        >
          {formatDate(operation.operationValidUntil)}
        </Typography>
      )}
    </Box>
  );
};

interface AircraftSubRowTitleProps {
  aircraft: AircraftSubRow;
  noOperation?: boolean;
  noOperationLabel?: string;
}

const aircraftSubRowSummarySx = {
  minHeight: "auto",
  py: 2,
  "&.Mui-expanded": {
    minHeight: "auto",
  },
  "& .MuiAccordionSummary-content": {
    my: 0,
    minWidth: 0,
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    my: 0,
  },
};

const AircraftSubRowTitle: FC<AircraftSubRowTitleProps> = ({ aircraft, noOperation, noOperationLabel }) => {
  const limitations = aircraft.limitations ?? [];
  const hasChips = limitations.length > 0 || noOperation;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", gap: 1, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            flex: "1 1 auto",
          }}
        >
          <Box sx={{ flexShrink: 0, display: "inline-flex" }}>
            <CourseStatusIcon
              status={aircraft.status}
              underSupervision={aircraft.underSupervision}
              hasLimitations={limitations.length > 0}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ ml: ".5rem", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
            noWrap
          >
            {aircraft.name}
          </Typography>
        </Box>
        {aircraft.validUntil && (
          <Typography variant="caption" sx={{ color: "#666", fontSize: "0.85em", flexShrink: 0 }}>
            {formatDate(aircraft.validUntil)}
          </Typography>
        )}
      </Box>
      {hasChips && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%", minWidth: 0 }}>
          {limitations.map((limitation) => (
            <Chip
              key={`${limitation.definitionId}:${limitation.displayValue}`}
              label={limitation.displayValue}
              size="small"
              sx={{ fontSize: "0.7rem", height: 20 }}
            />
          ))}
          {noOperation && (
            <Chip
              label={noOperationLabel}
              size="small"
              color="warning"
              variant="outlined"
              sx={{
                fontSize: "0.7rem",
                height: 22,
                "& .MuiChip-label": {
                  lineHeight: 1.4,
                },
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface AircraftProps {
  complianceData: ComplianceWithCourses;
  overviewRows: AircraftOverviewRow[];
  userId?: number;
  crewId?: number;
  history?: History;
}

const Aircraft: FC<AircraftProps> = ({ complianceData, overviewRows, userId, crewId, history }) => {
  const { t } = useTranslation();
  const aircraftOpsEnabled = useFeatureFlag(FEATURE_FLAGS.AIRCRAFT_OPS);
  const [flatView, setFlatView] = useState<boolean | null>(null);
  const [enabledOperationTracking, setEnabledOperationTracking] = useState<EnabledOperationTracking | null>(null);

  useEffect(() => {
    let ignore = false;

    const loadViewState = async () => {
      const [viewSetting, operationRecords]: [any, UserAircraftOperationRecord[]] = await Promise.all([
        getDefaultSettings(SettingKey.AIRCRAFT_VIEW_MODE),
        crewId && userId
          ? getAircraftOperationRecords({ crewId, userId }).catch((): UserAircraftOperationRecord[] => [])
          : Promise.resolve([] as UserAircraftOperationRecord[]),
      ]);

      if (ignore) return;
      const enabledRecords = operationRecords.filter((record) => record.enabled);
      setFlatView(viewSetting?.value?.flat !== false);
      setEnabledOperationTracking({
        operationKeys: new Set(enabledRecords.map((record) => `${record.typeId}:${record.operationId}`)),
        aircraftTypeIds: new Set(enabledRecords.map((record) => record.typeId)),
      });
    };

    loadViewState();

    return () => {
      ignore = true;
    };
  }, [crewId, userId]);

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (value === null) return;
    const next = value === "flat";
    setFlatView(next);
    saveDefaultSettings(SettingKey.AIRCRAFT_VIEW_MODE, { flat: next });
  };

  const relationTree = complianceData?.relationTree;
  const certificateMap = complianceData?.certificateMap;
  const overviewRowByAircraftId = useMemo(
    () => new Map(overviewRows.map((row) => [row.aircraftId, row])),
    [overviewRows],
  );

  // ── Flat view transform ────────────────────────────────────────────────────
  const flatAircraft = useMemo(() => {
    if (!relationTree) return [];
    const flatAircraftMap = new Map<number, AircraftSubRow>();
    const mergeAircraft = (a: any) => {
      const existing = flatAircraftMap.get(a.id);
      const aggregatedStatus = getWorstStatusNode([
        ...(existing ? [{ status: existing.status, validUntil: existing.validUntil }] : []),
        { status: a.status ?? null, validUntil: a.validUntil ?? null },
      ]);
      flatAircraftMap.set(a.id, {
        id: a.id,
        name: a.name ?? existing?.name ?? `Aircraft ${a.id}`,
        status: aggregatedStatus.status,
        validUntil: aggregatedStatus.validUntil,
        courseIds: Array.from(new Set([...(existing?.courseIds ?? []), ...(a.courseIds ?? [])])),
      });
    };
    relationTree.roles.forEach((role: any) => {
      role.aircraft?.forEach(mergeAircraft);
      role.positions?.forEach((pos: any) => {
        pos.aircraft?.forEach(mergeAircraft);
        pos.operations?.forEach((op: any) => {
          op.aircraft?.forEach(mergeAircraft);
        });
      });
    });
    return Array.from(flatAircraftMap.values());
  }, [relationTree]);

  // ── Operation-first transform ──────────────────────────────────────────────
  const positionGroups = useMemo(() => {
    if (!relationTree || !enabledOperationTracking) return [];
    const positionGroupMap = new Map<string, PositionGroup>();
    relationTree.roles.forEach((role) => {
      role.positions?.forEach((position: any) => {
        if (!position.operations || position.operations.length === 0) return;

        const positionAircraftById = new Map<number, any>();
        (position.aircraft || []).forEach((a: any) => positionAircraftById.set(a.id, a));

        const hasAnyAircraft =
          positionAircraftById.size > 0 || position.operations.some((op: any) => op.aircraft && op.aircraft.length > 0);
        if (!hasAnyAircraft) return;

        const posKey = `${role.id}-${position.id}`;
        if (!positionGroupMap.has(posKey)) {
          positionGroupMap.set(posKey, {
            key: posKey,
            roleName: role.name,
            positionName: position.name,
            operations: [],
          });
        }

        const group = positionGroupMap.get(posKey)!;

        position.operations.forEach((operation: any) => {
          // Only include aircraft that are explicitly enabled for this operation.
          // Position-level aircraft (positionAircraftById) are merged in only for types
          // that already appear at operation level — position courses are additional courses
          // for the same aircraft, not a reason to show a new aircraft row.
          const operationAircraftById = new Map<number, any>();
          (operation.aircraft || [])
            .filter((a: any) => enabledOperationTracking.operationKeys.has(`${a.id}:${operation.id}`))
            .forEach((a: any) => operationAircraftById.set(a.id, a));

          if (operationAircraftById.size === 0) return;

          const aircraft: AircraftSubRow[] = [];
          Array.from(operationAircraftById.keys()).forEach((typeId) => {
            const opAircraft = operationAircraftById.get(typeId);
            const posAircraft = positionAircraftById.get(typeId);
            const operationCompliance =
              overviewRowByAircraftId.get(typeId)?.operationCompliance[`${role.id}-${position.id}-${operation.id}`];
            const merged = Array.from(new Set([...(posAircraft?.courseIds || []), ...(opAircraft?.courseIds || [])]));
            aircraft.push({
              id: typeId,
              name: opAircraft?.name ?? posAircraft?.name ?? `Aircraft ${typeId}`,
              status: operationCompliance?.aircraft.status ?? opAircraft?.status ?? null,
              validUntil: operationCompliance?.aircraft.validUntil ?? opAircraft?.validUntil ?? null,
              courseIds: merged,
              underSupervision: operationCompliance?.underSupervision ?? false,
              limitations: operationCompliance?.limitations ?? [],
            });
          });

          if (aircraft.length === 0) return;
          const firstOperationCompliance = aircraft
            .map(
              (item) =>
                overviewRowByAircraftId.get(item.id)?.operationCompliance[`${role.id}-${position.id}-${operation.id}`],
            )
            .find(Boolean);

          group.operations.push({
            id: operation.id,
            name: operation.name,
            positionId: position.id,
            positionName: position.name,
            roleName: role.name,
            roleId: role.id,
            operationStatus: firstOperationCompliance?.operation.status ?? null,
            operationValidUntil: firstOperationCompliance?.operation.validUntil ?? null,
            aircraft,
          });
        });
      });
    });
    return Array.from(positionGroupMap.values());
  }, [enabledOperationTracking, overviewRowByAircraftId, relationTree]);

  // ── Expand/collapse IDs ────────────────────────────────────────────────────

  const allOperationAircraftIds = useMemo(() => {
    const ids: string[] = [];
    positionGroups.forEach((group) => {
      group.operations.forEach((op) => {
        op.aircraft.forEach((a) => ids.push(`op-${op.id}-aircraft-${a.id}`));
      });
    });
    return ids;
  }, [positionGroups]);

  const allFlatAircraftIds = useMemo(() => flatAircraft.map((a) => `flat-aircraft-${a.id}`), [flatAircraft]);

  const storageKey = `expand./crew/${crewId}/members/${userId}/compliance/aircraft`;
  const expandableState = useExpandableState({ storageKey, defaultExpanded: false });

  // A link from the Aircraft Overview (#op-<operationId>-aircraft-<typeId>) opens and scrolls to that operation's
  // aircraft row once the view is known; the flat view has one row per aircraft type instead
  const hashTarget = useRef(window.location.hash.slice(1));
  useEffect(() => {
    if (flatView === null || enabledOperationTracking === null) return;
    const typeId = /^op-\d+-aircraft-(\d+)$/.exec(hashTarget.current)?.[1];
    if (!typeId) return;

    const showsFlatView = flatView || !positionGroups.some((group) => group.operations.length > 0);
    const rowId = showsFlatView ? `flat-aircraft-${typeId}` : hashTarget.current;
    hashTarget.current = "";
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    expandableState.setExpanded(rowId, true);
    const element = document.getElementById(rowId);
    if (element) scrollToElement(element);
  }, [flatView, enabledOperationTracking]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Early returns (after all hooks) ───────────────────────────────────────

  if (flatView === null || enabledOperationTracking === null) return null;

  if (!relationTree) {
    return <Typography>{t("compliance.no_aircraft_data")}</Typography>;
  }

  const getCertificatesForCourseIds = (courseIds: string[]) => {
    if (!courseIds || courseIds.length === 0) return [];
    return courseIds.map((courseId) => certificateMap[courseId]).filter(Boolean);
  };

  const hasOperationView = positionGroups.some((g) => g.operations.length > 0);
  // Fall back to flat view when the operation-first view has nothing to show
  const effectiveFlatView = flatView || !hasOperationView;

  const activeIds = effectiveFlatView ? allFlatAircraftIds : allOperationAircraftIds;

  const handleToggleAll = () => {
    if (expandableState.hasAnyExpanded(activeIds)) {
      expandableState.collapseAll(activeIds);
    } else {
      expandableState.expandAll(activeIds);
    }
  };

  if (effectiveFlatView && flatAircraft.length === 0) {
    return <Typography>{t("compliance.no_aircraft_assigned")}</Typography>;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        {hasOperationView && (
          <ToggleButtonGroup
            value={effectiveFlatView ? "flat" : "operation"}
            exclusive
            onChange={handleViewChange}
            size="small"
          >
            <ToggleButton value="flat">{t("compliance.flat_view")}</ToggleButton>
            <ToggleButton value="operation">{t("compliance.operation_view")}</ToggleButton>
          </ToggleButtonGroup>
        )}
        <ExpandCollapseButton
          expanded={expandableState.hasAnyExpanded(activeIds)}
          onToggle={handleToggleAll}
          type="aircraft"
        />
      </Box>

      {effectiveFlatView ? (
        // ── Flat view ───────────────────────────────────────────────────────
        <>
          {flatAircraft.map((aircraft) => {
            const rowId = `flat-aircraft-${aircraft.id}`;
            return (
              <ExpandableSection
                key={rowId}
                title={
                  <AircraftSubRowTitle
                    aircraft={aircraft}
                    noOperation={aircraftOpsEnabled && !enabledOperationTracking.aircraftTypeIds.has(aircraft.id)}
                    noOperationLabel={t("compliance.no_operation_warning")}
                  />
                }
                navId={rowId}
                expanded={expandableState.isExpanded(rowId)}
                onChange={(expanded) => expandableState.setExpanded(rowId, expanded)}
                summarySx={aircraftSubRowSummarySx}
              >
                <Box
                  sx={{
                    ml: 2,
                  }}
                >
                  {aircraft.courseIds.length > 0 ? (
                    <CertificateList certificates={getCertificatesForCourseIds(aircraft.courseIds)} variant="default" />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {t("compliance.no_aircraft_courses")}
                    </Typography>
                  )}
                </Box>
              </ExpandableSection>
            );
          })}
        </>
      ) : (
        // ── Operation-first view ────────────────────────────────────────────
        <>
          <Hint settingKey={SettingKey.AIRCRAFT_OPERATION_VIEW_HINT} sx={{ mb: 2 }}>
            <>
              {t("compliance.operation_view_hint")}
              <Box
                sx={{
                  mt: 1,
                }}
              >
                <img
                  src={aircraftByOperationImg}
                  alt=""
                  style={{ maxWidth: "75%", borderRadius: 4, display: "block" }}
                />
              </Box>
            </>
          </Hint>
          {positionGroups.map((group) => (
            <Box
              key={group.key}
              sx={{
                mb: 3,
              }}
            >
              {group.operations.map((operation) => (
                <Box
                  key={`op-${operation.id}`}
                  sx={{
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <OperationCardTitle operation={operation} crewId={crewId} userId={userId} history={history} />
                  </Box>

                  <Box
                    sx={{
                      ml: 2,
                      mt: 0.5,
                    }}
                  >
                    {operation.aircraft.map((aircraft) => {
                      const rowId = `op-${operation.id}-aircraft-${aircraft.id}`;
                      return (
                        <ExpandableSection
                          key={rowId}
                          title={<AircraftSubRowTitle aircraft={aircraft} />}
                          navId={rowId}
                          expanded={expandableState.isExpanded(rowId)}
                          onChange={(expanded) => expandableState.setExpanded(rowId, expanded)}
                          summarySx={aircraftSubRowSummarySx}
                        >
                          <Box
                            sx={{
                              ml: 2,
                            }}
                          >
                            {aircraft.courseIds.length > 0 ? (
                              <CertificateList
                                certificates={getCertificatesForCourseIds(aircraft.courseIds)}
                                variant="default"
                              />
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                {t("compliance.no_aircraft_courses")}
                              </Typography>
                            )}
                          </Box>
                        </ExpandableSection>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </>
      )}
    </>
  );
};

export default Aircraft;
